import { Journey } from '@audibene/ta.utils-opportunity'
import { Button } from '@mui/material'
import { useSnackbar, SnackbarKey } from 'notistack'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Country } from '../../constants'
import { AppointmentTypes, QueryOptions } from '../../types'
import { logFactory } from '../../util/logFactory'
import { useAppointmentsService } from '../services'
import { CloseButton } from '../useNotification/CloseButton'
import { useNotification } from '../useNotification'

interface UseAppointmentTypeOptionsContext {
  appointmentTypes?: AppointmentTypes
  isError: boolean
  isLoading: boolean
}

interface Props {
  country: Country
  journey: Journey
}

const hookName = 'useAppointmentTypeOptions'
const log = logFactory(hookName)
const defaults: QueryOptions = { enabled: true }

const MAX_REQUEST_APPOINTMENT_TYPES_RETRIES = 2

const useAppointmentTypeOptions = (query: Props, options: QueryOptions = {}): UseAppointmentTypeOptionsContext => {
  const actualOptions = useMemo<QueryOptions>(() => ({ ...defaults, ...options }), [options])
  const { getAppointmentTypes, isReady } = useAppointmentsService()
  const { notify } = useNotification()
  const { closeSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [retries, setRetries] = useState(0)

  const fetchAppointmentTypes = useCallback(async (): Promise<AppointmentTypes> => {
    log('🗓️ Fetching appointment types...', query)
    const found = await getAppointmentTypes(query)
    log('🗓️ Found appointment types', found)
    return found
  }, [getAppointmentTypes, query])

  const isQueryEnabled = Boolean(query.country && query.journey && isReady && actualOptions.enabled)

  const {
    data: appointmentTypes,
    isError,
    isLoading,
    refetch,
  } = useQuery([hookName, query], fetchAppointmentTypes, {
    ...actualOptions,
    enabled: isQueryEnabled,
    retry: false,
    staleTime: Infinity,
    cacheTime: Infinity, // keep the data in the cache during session
  })

  const handleRetry = useCallback(() => {
    setRetries((r) => r + 1)
    refetch()
    closeSnackbar()
  }, [closeSnackbar, refetch, setRetries])

  const showSnackbar = useCallback(() => {
    const action = (
      <>
        <Button variant="outlined" size="small" color="inherit" onClick={handleRetry}>
          {t('common.label.tryAgain')}
        </Button>
        <CloseButton />
      </>
    )

    notify({ message: t('common.message.appointmentTypesRequestFailed'), type: 'UNHANDLED_ERROR' as any }, 'error', {
      autoHideDuration: null,
      hideIconVariant: true,
      persist: true,
      preventDuplicate: true,
      action,
    }) as SnackbarKey | undefined
  }, [handleRetry, notify, retries])

  // Show snackbar to retry or show error after all retries
  useEffect(() => {
    if (isError && !isLoading && isQueryEnabled) {
      if (retries < MAX_REQUEST_APPOINTMENT_TYPES_RETRIES) {
        showSnackbar()
      } else {
        notify({ message: t('common.message.appointmentTypesRequestFailedTryLater'), type: 'UNHANDLED_ERROR' as any }, 'error', {
          autoHideDuration: null,
          hideIconVariant: true,
          persist: true,
          preventDuplicate: true,
        })
      }
    }
  }, [isError, isLoading, isQueryEnabled, retries, notify, showSnackbar])

  return {
    appointmentTypes,
    isError,
    isLoading: (!isReady || isLoading) && !appointmentTypes,
  }
}

export { useAppointmentTypeOptions }
